import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { fetchReadingTypes, fetchDataTypes, fetchSensors, updateReadingType } from '../../fetchers/readingType';
import AxionControls from "../../components/controls/AxionControls";
import SensorDataDiagramSettingsGrid from './components/sensorDataDiagramSettingsGrid';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FormattedMessage } from 'react-intl';
import messages from './components/messages';
import Popup from "../../components/Popup";
import ReadingTypeDialog from './components/readingTypeDialog';
import ReadingThresholdDialog from './components/readingThresholdDialog';
import { pushErrorMessage } from '../../utils/notifications';
import { fetchReadingThresholds, updateReadingThreshold } from '../../fetchers/readingthreshold';
import { fetchReadingAllThresholdsForMachine } from '../../fetchers/readingthreshold';
import useFeatureFlags, { isFeatureEnabled } from "../../hooks/useFeatureFlags";
import { fetchAllInstructions } from '../instructions/hooks/fetchers';

export default function SensorDataDiagramSettings(props) {
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [showBackdrop, setShowBackdrop] = useState(false);
    const [openSettingsPopup, setOpenSettingsPopup] = useState(false);
    const [openThresholdPopup, setOpenThresholdPopup] = useState(false);
    const [readingType, setReadingType] = useState({});
    const [readingThresholds, setReadingThresholds] = useState(
        {
            readingTypeId: null,
            enableHighLimit: false,
            highLimitValue: null,
            highLimitInstructionId: null,
            highLimitMinutesOverThreshold: 0,
            highLimitCheckMachineInStatus: false,
            highLimitMachineInRunningStatus: false,
            enableLowLimit: false,
            lowLimitValue: null,
            lowLimitInstructionId: null,
            lowLimitMinutesOverThreshold: 0,
            lowLimitCheckMachineInStatus: false,
            lowLimitMachineInRunningStatus: false,
        }
    );
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const readingTypesQuyery = useQuery({
        queryKey: ['readingTypes', id],
        queryFn: () => fetchReadingTypes(id),
        staleTime: 60000,
        refetchInterval: 60000,
    });

    const dataTypesQuery = useQuery(
        ["dataTypes"],
        () => fetchDataTypes(),
        {
            staleTime: 60000,
            refetchInterval: 60000
        });

    const sensorsQuery = useQuery(
        ["sensors"],
        () => fetchSensors(),
        {
            staleTime: 60000,
            refetchInterval: 60000
        });

    const thresholdQuery = useQuery(
        ["thresholds", id],
        () => fetchReadingAllThresholdsForMachine(id),
        {
            staleTime: 60000,
            refetchInterval: 60000
        }
    );

    const featuresQuery = useFeatureFlags();
        
    const InstructionsQuery = useQuery(
        ["allInstructions", companyKey],
        () => fetchAllInstructions(companyKey),
        {
            staleTime: 60000,
            refetchInterval: 60000
        }
    );

    const handleEditThresholdClick = (readingType) => {
        setReadingType(readingType);
        fetchReadingThresholds(readingType.id).then((response) => {
            setReadingThresholds(response);
            setOpenThresholdPopup(true);
        });
    }

    const handleEditThresholdCancel = () => {
        setOpenThresholdPopup(false);
    }

    const handleEditReadingTypeClick = (readingType) => {
        setReadingType(readingType);
        setOpenSettingsPopup(true);
    }

    const handleThresholdSave = () => {
        setShowBackdrop(true);
        updateReadingThreshold(readingThresholds).then(() => {
            setOpenThresholdPopup(false);
            thresholdQuery.refetch();
            setShowBackdrop(false);
        })
            .catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Could not update the sensor threshold settings.`);
            });
    }

    const handleReadingTypeSave = () => {
        setShowBackdrop(true);
        updateReadingType(readingType).then(() => {
            setOpenSettingsPopup(false);
            readingTypesQuyery.refetch();

            queryClient.invalidateQueries({
                queryKey: ["sensorData", id],
                exact: false 
            });

            setShowBackdrop(false);
        })
            .catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Could not update the sensor diagram value settings.`);
            });
    }

    const handleReadingTypeCancel = () => {
        setOpenSettingsPopup(false);
    }


    if (readingTypesQuyery?.isLoading || dataTypesQuery?.isLoading || sensorsQuery?.isLoading || thresholdQuery?.isLoading || InstructionsQuery?.isLoading || featuresQuery?.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    }

    return (
        <>
            <Box mt={2} ml={2}>
                <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.backButtonTooltip} /></Typography>}>
                    <IconButton aria-label="sensor data settings" size="large" onClick={() => { navigate(`/machines/${id}/?tab=5`); }}>
                        <ArrowBackIcon fontSize="inherit" />
                    </IconButton>
                </Tooltip>
            </Box>
            <SensorDataDiagramSettingsGrid
                readingTypesResult={readingTypesQuyery}
                dataTypesResult={dataTypesQuery}
                sensorsResult={sensorsQuery}
                thresholdResult={thresholdQuery}
                handleEditReadingTypeClick={handleEditReadingTypeClick}
                handleEditThresholdClick={handleEditThresholdClick}
            />
            <Popup
                size="md"
                openPopup={openSettingsPopup}
                setOpenPopup={setOpenSettingsPopup}
                title={<FormattedMessage {...messages.editReadingType} />}>
                <ReadingTypeDialog
                    setOpenPopup={setOpenSettingsPopup}
                    dataTypes={dataTypesQuery.data}
                    sensors={sensorsQuery.data}
                    readingType={readingType}
                    setReadingType={setReadingType}
                    onReadingTypeSave={handleReadingTypeSave}
                    onReadingTypeCancel={handleReadingTypeCancel} />
            </Popup>

            <Popup
                size="md"
                openPopup={openThresholdPopup}
                setOpenPopup={setOpenThresholdPopup}
                title={<FormattedMessage {...messages.editThresholds} />}>
                <ReadingThresholdDialog
                    setOpenPopup={setOpenThresholdPopup}
                    readingThresholds={readingThresholds}
                    setReadingThresholds={setReadingThresholds}
                    onThresholdSave={handleThresholdSave}
                    onEditThresholdCancel={handleEditThresholdCancel}
                    instructionsResult={InstructionsQuery.data?.filter(x => x.type === 2) || []}
                />
            </Popup>
        </>
    )
}